import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject,
} from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import { colors, spacings } from "../Theme";
import { AddLine, RestartLine, SubtractFill } from "../IconV2/react";
import TooltipV2 from "../TooltipV2";
import t from "../i18n";
import { precision } from "../lib/linear_interpolation";
import Menu from "../Menu";

interface Props {
  src: string;
  alt: string;
  viewportConfig?: ViewportConfig;
  onViewportChange?: (config: ViewportConfig) => void;
  controls?: boolean;
  className?: string;
  menu?: React.ComponentProps<typeof ImageViewerControls>["menu"];
}

type ViewportConfig = {
  scale: number; // 0 is max zoom out, 1 is 0% zoom
  centerOffset: { x: number; y: number }; // x:0,y:0 is default
};

/**
 * ImageViewer component displays an image within a viewport that can be zoomed and panned.
 * The image has an overlay over top that contains annotations.
 *
 * Refs:
 * root - the main viewport
 * img - Also known as target, the image element that is displayed
 * overlay - The object over the image that contains the annotations
 */

export const ImageViewer = memo(
  ({
    src,
    alt,
    controls,
    className,
    children,
    menu,
    onViewportChange,
    viewportConfig,
    ...props
  }: PropsWithChildren<Props>) => {
    const viewport = useRef<HTMLDivElement>(null);
    const img = useRef<HTMLImageElement>(null);
    const overlay = useRef<HTMLDivElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
      /** @note download budget time: 1s */
      setTimeout(() => setImageLoaded(true), 1000);
    }, [setImageLoaded]);

    return (
      <div ref={viewport} className={cx(imageViewerCss, { controls }, className)} {...props}>
        {src && (
          <img
            ref={img}
            src={src}
            alt={alt}
            loading="eager"
            draggable={!controls}
            onLoadCapture={() => setImageLoaded(true)}
          />
        )}
        <div ref={overlay} className={imageViewerOverlayCss}>
          {imageLoaded && children}
        </div>
        {controls && (
          <ImageViewerControls
            onViewportChange={onViewportChange}
            viewport={viewport}
            img={img}
            overlay={overlay}
            menu={menu}
            viewportConfig={viewportConfig}
          />
        )}
      </div>
    );
  },
);

function ImageViewerControls({
  viewport,
  img,
  overlay,
  menu,
  onViewportChange,
  viewportConfig = { scale: 1, centerOffset: { x: 0, y: 0 } },
}: {
  viewport: RefObject<HTMLDivElement>;
  img: RefObject<HTMLImageElement>;
  overlay: RefObject<HTMLDivElement>;
  menu?: React.ComponentProps<typeof Menu>["overlay"];
  onViewportChange?: (config: ViewportConfig) => void;
  viewportConfig?: ViewportConfig;
}) {
  const zoomInRef = useRef<HTMLButtonElement>(null);
  const zoomOutRef = useRef<HTMLButtonElement>(null);
  const resetRef = useRef<HTMLButtonElement>(null);
  const position = useRef<{ x: number; y: number }>({
    x: viewportConfig.centerOffset.x,
    y: viewportConfig.centerOffset.y,
  });

  const scale = useRef(viewportConfig.scale);

  const onChange = useCallback(
    (config: ViewportConfig) => {
      const scaleUpdated = config.scale !== viewportConfig.scale;
      const positionUpdated =
        config.centerOffset.x !== viewportConfig.centerOffset.x ||
        config.centerOffset.y !== viewportConfig.centerOffset.y;

      if (scaleUpdated || positionUpdated) {
        resetRef.current?.removeAttribute("disabled");
      } else {
        resetRef.current?.setAttribute("disabled", "true");
      }
      onViewportChange?.(config);
    },
    [
      onViewportChange,
      viewportConfig.centerOffset.x,
      viewportConfig.centerOffset.y,
      viewportConfig.scale,
    ],
  );

  const applyScale = useCallback(
    (newScale: number) => {
      const previousScale = scale.current;

      if (newScale <= 0 || previousScale <= 0) return;

      const ratio = newScale / previousScale;
      scale.current = precision(newScale, 4);
      position.current.x = precision(position.current.x * ratio, 4);
      position.current.y = precision(position.current.y * ratio, 4);

      onChange({
        scale: scale.current,
        centerOffset: {
          x: position.current.x,
          y: position.current.y,
        },
      });
    },
    [onChange],
  );

  const zoomIn = useCallback(() => {
    applyScale(scale.current + 0.1);
  }, [applyScale]);

  const zoomOut = useCallback(() => {
    const newScale = Math.max(0, scale.current - 0.1);

    applyScale(newScale);
  }, [applyScale]);

  const reset = useCallback(() => {
    scale.current = viewportConfig.scale;
    position.current.x = viewportConfig.centerOffset.x;
    position.current.y = viewportConfig.centerOffset.y;
    resetRef.current?.setAttribute("disabled", "true");
    onChange?.({ scale: scale.current, centerOffset: position.current });
  }, [viewportConfig, onChange]);

  useEffect(() => {
    if (!viewport.current || !img.current || !overlay.current) return;
    const $viewport = viewport.current;
    const $img = img.current;
    const $overlay = overlay.current;

    resetRef.current?.setAttribute("disabled", "true");
    $overlay.style.opacity = "1";
    $img.style.opacity = "1";

    $viewport.addEventListener("pointerdown", start);
    function start(pointerDownEvent: PointerEvent) {
      pointerDownEvent.stopPropagation();
      const ctrl = new AbortController();
      const x = position.current.x;
      const y = position.current.y;

      $viewport.style.cursor = "grabbing";
      $viewport.addEventListener("pointermove", drag, { passive: true, signal: ctrl.signal });
      function drag(pointerMoveEvent: PointerEvent) {
        position.current.x = pointerMoveEvent.clientX - pointerDownEvent.clientX + x;
        position.current.y = pointerMoveEvent.clientY - pointerDownEvent.clientY + y;

        if (
          position.current.x !== 0 ||
          (position.current.y !== 0 && resetRef.current?.hasAttribute("disabled"))
        ) {
          resetRef.current?.removeAttribute("disabled");
        }
      }

      $viewport.addEventListener("pointerup", stop, { once: true });
      function stop() {
        $viewport.style.cursor = "grab";
        onChange?.({
          scale: scale.current,
          centerOffset: {
            x: precision(position.current.x, 4),
            y: precision(position.current.y, 4),
          },
        });
        ctrl.abort();
      }
    }

    let lastScale = 0;
    let lastImgNaturalHeight = 0;
    let frameId = window.requestAnimationFrame(viewPortOffsetLoop);
    let x: number = viewportConfig.centerOffset.x;
    let y: number = viewportConfig.centerOffset.y;
    let s: number = viewportConfig.scale;
    let width = 0;
    let height = 0;
    function viewPortOffsetLoop() {
      x = precision(position.current.x, 4);
      y = precision(position.current.y, 4);
      s = scale.current;
      height = $img.naturalHeight * s;
      width = $img.naturalWidth * s;
      const transformX = x - width / 2;
      const transformY = y - height / 2;

      $img.style.height = `${$img.naturalHeight}px`;
      $img.style.width = `${$img.naturalWidth}px`;
      $img.style.transform = `matrix(${s}, 0, 0, ${s}, ${transformX}, ${transformY})`;
      if ($overlay) {
        $overlay.style.width = `${width}px`;
        $overlay.style.height = `${height}px`;
        $overlay.style.transform = `matrix(1, 0, 0, 1, ${transformX}, ${transformY})`;
        $overlay.style.setProperty("--image-viewer-scale", String(s));

        const imgNaturalHeight = $img.naturalHeight;
        if (scale.current !== lastScale || imgNaturalHeight !== lastImgNaturalHeight) {
          lastScale = scale.current;
          lastImgNaturalHeight = $img.naturalHeight;
        }
      }

      frameId = window.requestAnimationFrame(viewPortOffsetLoop);
    }

    const resizeScaleObserver = new ResizeObserver(() => (lastScale = 0));
    resizeScaleObserver.observe($viewport);

    return () => {
      $viewport.removeEventListener("pointerdown", start);
      resizeScaleObserver.disconnect();
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [overlay, viewportConfig, viewport, img, onChange]);

  return (
    <div className={imageViewerControlsCss}>
      {menu && (
        <div className={controlMenuButtonCss}>{typeof menu === "function" ? menu() : menu}</div>
      )}
      <div>
        <TooltipV2 overlay={t("image_reader.zoom_in")} placement="left">
          <button className={controlButtonCss} ref={zoomInRef} onClick={zoomIn}>
            <AddLine />
          </button>
        </TooltipV2>
        <TooltipV2 overlay={t("image_reader.zoom_out")} placement="left">
          <button className={controlButtonCss} ref={zoomOutRef} onClick={zoomOut}>
            <SubtractFill />
          </button>
        </TooltipV2>
        <TooltipV2 overlay={t("image_reader.reset")} placement="left">
          <button className={cx(controlButtonCss)} ref={resetRef} onClick={reset}>
            <RestartLine />
          </button>
        </TooltipV2>
      </div>
    </div>
  );
}

export const overlayBoundary = "overlayBoundary";

const imageViewerCss = css`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 0;
  justify-content: center;

  &.controls {
    cursor: grab;

    img {
      will-change: transform;
    }
  }

  img {
    position: absolute;
    pointer-events: none;
    max-width: none;
    max-height: none;
    transform-origin: top left;
    top: 50%;
    left: 50%;
    user-select: none;
    opacity: 0;
    transition: opacity 150ms linear;
  }
`;

const imageViewerOverlayCss = css`
  position: absolute;
  transform-origin: top left;
  top: 50%;
  left: 50%;
  z-index: 1;
  opacity: 0;
  transition: opacity 150ms linear;
`;

const imageViewerControlsCss = css`
  position: absolute;
  right: ${spacings.xs};
  top: 50%;
  transition: opacity 0.15s linear;
  transform: translateY(-50%);
  z-index: 2;
  text-align: center;
`;
const controlMenuButtonCss = css`
  margin-bottom: ${spacings.xs};
`;

const controlButtonCss = css`
  align-items: center;
  background-color: ${colors.hydrogen};
  border: 1px solid ${colors.titanium200};
  cursor: pointer;
  display: flex;
  height: 35px;
  justify-content: center;
  padding: 0;
  width: 35px;

  &:disabled {
    cursor: not-allowed;

    svg {
      opacity: 0.5;
    }
  }

  &:first-child {
    border-top-left-radius: ${spacings.xs};
    border-top-right-radius: ${spacings.xs};
  }

  &:not(:first-child) {
    border-top: unset;
  }

  &:last-child {
    border-bottom-left-radius: ${spacings.xs};
    border-bottom-right-radius: ${spacings.xs};
  }
`;
